'use client';

import { Envelope } from '@sprd/sprd-component-kit/v2';
import { AlertCircle } from '@sprd/sprd-component-kit/v2';
import { InfoCircle } from '@sprd/sprd-component-kit/v2';
import { Button } from '@sprd/sprd-component-kit/v2';
import { useActionState, useState } from 'react';
import { FooterContent } from '@/types/amplience';
import {
  type NewsletterSignUpResult,
  newsletterSignUpReducer,
} from '@/app/actions/newsletterSignUp';
import { Locale } from '@/utility/locale';

interface NewsletterFormClientProps {
  content: FooterContent;
  locale: Locale;
  formHeader: React.ReactNode;
}

export default function NewsletterFormClient({
  content,
  locale,
  formHeader,
}: NewsletterFormClientProps) {
  const [email, setEmail] = useState('');
  const [result, action, isPending] = useActionState<NewsletterSignUpResult | null, FormData>(
    newsletterSignUpReducer,
    null
  );

  const status: 'idle' | 'loading' | 'success' | 'error' = isPending
    ? 'loading'
    : result
      ? result.ok
        ? 'success'
        : 'error'
      : 'idle';
  return (
    <div className="flex w-full max-w-170 flex-col items-center py-4 sm:w-170 md:py-6">
      {status !== 'success' ? (
        <>
          {formHeader}

          <form action={action} className="flex w-full flex-col items-center">
            {/* Canonical locale; the server action derives the wire format. */}
            <input type="hidden" name="locale" value={locale} />
            <div className="relative w-full sm:w-150">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 w-full border-2 border-solid border-neutral-700 bg-white px-2 py-2 text-sm text-neutral-700 sm:min-w-104.5 md:min-w-145"
                placeholder={content.content.newsletter.placeholder}
                required
              />

              {status === 'error' ? (
                <>
                  <AlertCircle
                    height="1.5rem"
                    width="1.5rem"
                    className="absolute top-3 right-3 fill-black"
                  />
                  <div className="mt-2 flex items-start gap-2 text-white sm:items-center">
                    <InfoCircle className="h-5 w-5 shrink-0 text-white" />
                    <div className="max-w-145 text-left text-xs">
                      {content.content.newsletter.errorMessage}
                    </div>
                  </div>
                </>
              ) : (
                <Envelope
                  height="1.5rem"
                  width="1.5rem"
                  className="absolute top-3 right-3 fill-black"
                />
              )}
            </div>

            {content.content.newsletter.button?.label && (
              <Button
                type="submit"
                size="l"
                disabled={isPending}
                className="mt-4 w-full border-2 border-solid border-white px-6 py-3 font-semibold text-white sm:w-fit md:mt-6"
                data-tracking={content.content.newsletter.button.tracking}
              >
                {content.content.newsletter.button?.label}
              </Button>
            )}
          </form>
        </>
      ) : (
        <>
          <h2 className="w-70 pb-2 text-xl font-bold text-white uppercase sm:w-100 sm:text-2xl md:text-[1.75rem] lg:text-[2rem] xl:text-4xl">
            {content.content.newsletter.successHeading}
          </h2>
          <p className="pb-10 text-sm text-white sm:text-base">
            {content.content.newsletter.successText}
          </p>
        </>
      )}
    </div>
  );
}
